import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit as queryLimit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
  type DocumentData,
  type DocumentReference,
} from 'firebase/firestore'
import type { z } from 'zod'
import { db } from '../utils/firebase'
import {
  actionSchema,
  automationReceiptSchema,
  captureSchema,
  contextRelationSchema,
  contextRecordSchema,
  planSchema,
  reviewItemSchema,
  signalSchema,
  type Action,
  type AutomationReceipt,
  type Capture,
  type ContextRelation,
  type ContextRecord,
  type Plan,
  type ReviewItem,
  type Signal,
} from '../types/core'

export const CORE_COLLECTIONS = {
  captures: 'captures',
  contextRecords: 'contextRecords',
  contextRelations: 'contextRelations',
  plans: 'plans',
  actions: 'actions',
  reviewItems: 'reviewItems',
  signals: 'signals',
  automationReceipts: 'automationReceipts',
} as const

type CoreRecord = Capture | ContextRecord | ContextRelation | Plan | Action | ReviewItem | Signal | AutomationReceipt

const persistedDateFields = new Set([
  'createdAt',
  'updatedAt',
  'processedAt',
  'targetStartAt',
  'targetEndAt',
  'completedAt',
  'dueAt',
  'scheduledFor',
  'resolvedAt',
  'surfacedAt',
  'expiresAt',
  'seenAt',
  'revertedAt',
  'observedAt',
  'startedAt',
  'endedAt',
])

const mapTopLevelDates = (
  record: Record<string, unknown>,
  transform: (value: unknown) => unknown,
): Record<string, unknown> => Object.fromEntries(
  Object.entries(record).map(([key, value]) => [
    key,
    persistedDateFields.has(key) ? transform(value) : value,
  ]),
)

const mapPersistedDates = (
  record: Record<string, unknown>,
  transform: (value: unknown) => unknown,
): Record<string, unknown> => {
  const mapped = mapTopLevelDates(record, transform)
  if (!mapped.observation || typeof mapped.observation !== 'object' || Array.isArray(mapped.observation)) {
    return mapped
  }

  return {
    ...mapped,
    observation: mapTopLevelDates(mapped.observation as Record<string, unknown>, transform),
  }
}

const stripUndefined = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.filter((entry) => entry !== undefined).map(stripUndefined)
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== undefined)
        .map(([key, entry]) => [key, stripUndefined(entry)]),
    )
  }

  return value
}

type EncodeTimestampMode = 'record' | 'create' | 'update'

export const encodeCoreRecord = (
  record: CoreRecord,
  timestampMode: EncodeTimestampMode = 'record',
  persistedCreatedAt?: unknown,
): DocumentData => {
  const sanitized = stripUndefined(record) as Record<string, unknown>
  const encoded = mapPersistedDates(
    sanitized,
    (value) => typeof value === 'string' ? Timestamp.fromDate(new Date(value)) : value,
  )

  if (timestampMode === 'create') {
    return { ...encoded, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }
  }

  if (timestampMode === 'update') {
    return { ...encoded, createdAt: persistedCreatedAt, updatedAt: serverTimestamp() }
  }

  return encoded
}

export const decodeCoreRecord = (record: DocumentData): Record<string, unknown> => mapPersistedDates(
  record,
  (value) => value instanceof Timestamp ? value.toDate().toISOString() : value,
)

class CoreCollectionRepository<T extends CoreRecord> {
  constructor(
    readonly collectionName: string,
    private readonly schema: z.ZodType<T, z.ZodTypeDef, unknown>,
  ) {}

  reference(id: string): DocumentReference {
    return doc(db, this.collectionName, id)
  }

  parse(raw: DocumentData): T {
    return this.schema.parse(decodeCoreRecord(raw))
  }

  async get(id: string): Promise<T | null> {
    const snapshot = await getDoc(this.reference(id))
    return snapshot.exists() ? this.parse(snapshot.data()) : null
  }

  async listForUser(userId: string, limitCount = 100): Promise<T[]> {
    const normalizedLimit = Number.isFinite(limitCount) ? Math.trunc(limitCount) : 100
    const boundedLimit = Math.max(1, Math.min(normalizedLimit, 500))
    const snapshot = await getDocs(query(
      collection(db, this.collectionName),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc'),
      queryLimit(boundedLimit),
    ))

    return snapshot.docs.map((entry) => this.parse(entry.data()))
  }

  async create(record: T): Promise<T> {
    const validated = this.schema.parse(record)
    const reference = this.reference(validated.id)
    await setDoc(reference, encodeCoreRecord(validated, 'create'))
    const persisted = await this.get(validated.id)
    if (!persisted) {
      throw new Error(`Core record ${validated.id} was not available after creating.`)
    }
    return persisted
  }

  async update(record: T): Promise<T> {
    const validated = this.schema.parse(record)
    const reference = this.reference(validated.id)
    await runTransaction(db, async (transaction) => {
      const existing = await transaction.get(reference)
      if (!existing.exists()) {
        throw new Error(`Core record ${validated.id} was not found for update.`)
      }
      transaction.set(
        reference,
        encodeCoreRecord(
          validated,
          'update',
          existing.data().createdAt,
        ),
      )
    })
    const persisted = await this.get(validated.id)
    if (!persisted) {
      throw new Error(`Core record ${validated.id} was not available after updating.`)
    }
    return persisted
  }

  async delete(id: string): Promise<void> {
    await deleteDoc(this.reference(id))
  }
}

export const coreRepositories = {
  captures: new CoreCollectionRepository<Capture>(CORE_COLLECTIONS.captures, captureSchema),
  contextRecords: new CoreCollectionRepository<ContextRecord>(CORE_COLLECTIONS.contextRecords, contextRecordSchema),
  contextRelations: new CoreCollectionRepository<ContextRelation>(CORE_COLLECTIONS.contextRelations, contextRelationSchema),
  plans: new CoreCollectionRepository<Plan>(CORE_COLLECTIONS.plans, planSchema),
  actions: new CoreCollectionRepository<Action>(CORE_COLLECTIONS.actions, actionSchema),
  reviewItems: new CoreCollectionRepository<ReviewItem>(CORE_COLLECTIONS.reviewItems, reviewItemSchema),
  signals: new CoreCollectionRepository<Signal>(CORE_COLLECTIONS.signals, signalSchema),
  automationReceipts: new CoreCollectionRepository<AutomationReceipt>(CORE_COLLECTIONS.automationReceipts, automationReceiptSchema),
}

export type CaptureAutomationWrite = {
  capture: Capture
  expectedCaptureUpdatedAt: string
  existingContextRecordIds?: string[]
  contextRecords?: ContextRecord[]
  contextRelations?: ContextRelation[]
  plans?: Plan[]
  actions?: Action[]
  reviewItems?: ReviewItem[]
  signals?: Signal[]
  receipt: AutomationReceipt
}

export type ReviewResolutionWrite = {
  reviewItem: ReviewItem
  expectedReviewUpdatedAt: string
  capture: Capture
  expectedCaptureUpdatedAt: string
  receipt: AutomationReceipt
  expectedReceiptUpdatedAt: string
  action?: Action
  contextRecord?: ContextRecord
  expectedContextUpdatedAt?: string
}

const assertSameOwner = (userId: string, records: CoreRecord[]) => {
  if (records.some((record) => record.userId !== userId)) {
    throw new Error('Automation outputs must belong to the capture owner.')
  }
}

export const applyCaptureAutomation = async (
  write: CaptureAutomationWrite,
): Promise<{ applied: boolean }> => {
  const capture = captureSchema.parse(write.capture)
  const contextRecords = (write.contextRecords ?? []).map((record) => contextRecordSchema.parse(record))
  const contextRelations = (write.contextRelations ?? []).map((record) => contextRelationSchema.parse(record))
  const plans = (write.plans ?? []).map((record) => planSchema.parse(record))
  const actions = (write.actions ?? []).map((record) => actionSchema.parse(record))
  const reviewItems = (write.reviewItems ?? []).map((record) => reviewItemSchema.parse(record))
  const signals = (write.signals ?? []).map((record) => signalSchema.parse(record))
  const receipt = automationReceiptSchema.parse(write.receipt)
  const outputs: CoreRecord[] = [...contextRecords, ...contextRelations, ...plans, ...actions, ...reviewItems, ...signals]
  const existingContextRecordIds = new Set(write.existingContextRecordIds ?? [])

  assertSameOwner(capture.userId, [receipt, ...outputs])

  if ([...existingContextRecordIds].some((id) => !contextRecords.some((record) => record.id === id))) {
    throw new Error('Existing context IDs must be included in the automation outputs.')
  }

  if (outputs.length + 2 > 450) {
    throw new Error('One automation receipt cannot write more than 450 records.')
  }

  return runTransaction(db, async (transaction) => {
    const receiptRef = coreRepositories.automationReceipts.reference(receipt.id)
    const captureRef = coreRepositories.captures.reference(capture.id)
    const existingCapture = await transaction.get(captureRef)

    if (!existingCapture.exists()) {
      throw new Error('Capture must be saved before automation can be applied.')
    }

    const persistedCapture = coreRepositories.captures.parse(existingCapture.data())
    if (persistedCapture.status !== 'saved' && persistedCapture.status !== 'failed') {
      return { applied: false }
    }

    if (persistedCapture.updatedAt !== write.expectedCaptureUpdatedAt) {
      throw new Error('Capture changed while automation was processing.')
    }

    if (persistedCapture.userId !== capture.userId) {
      throw new Error('Automation capture owner does not match the saved capture.')
    }

    const existingContextSnapshots = await Promise.all(
      [...existingContextRecordIds].map(async (id) => ({
        id,
        snapshot: await transaction.get(coreRepositories.contextRecords.reference(id)),
      })),
    )

    existingContextSnapshots.forEach(({ id, snapshot }) => {
      if (!snapshot.exists()) {
        throw new Error(`Existing context ${id} was not found during automation.`)
      }
      const persisted = coreRepositories.contextRecords.parse(snapshot.data())
      if (persisted.userId !== capture.userId) {
        throw new Error('Automation cannot update context owned by another user.')
      }
    })
    const contextSnapshotsById = new Map(
      existingContextSnapshots.map(({ id, snapshot }) => [id, snapshot]),
    )

    transaction.set(
      captureRef,
      encodeCoreRecord(capture, 'update', existingCapture.data().createdAt),
    )
    contextRecords.forEach((record) => {
      const existing = contextSnapshotsById.get(record.id)
      transaction.set(
        coreRepositories.contextRecords.reference(record.id),
        encodeCoreRecord(
          record,
          existing ? 'update' : 'create',
          existing?.data().createdAt,
        ),
      )
    })
    contextRelations.forEach((record) => transaction.set(
      coreRepositories.contextRelations.reference(record.id),
      encodeCoreRecord(record, 'create'),
    ))
    plans.forEach((record) => transaction.set(
      coreRepositories.plans.reference(record.id),
      encodeCoreRecord(record, 'create'),
    ))
    actions.forEach((record) => transaction.set(
      coreRepositories.actions.reference(record.id),
      encodeCoreRecord(record, 'create'),
    ))
    reviewItems.forEach((record) => transaction.set(
      coreRepositories.reviewItems.reference(record.id),
      encodeCoreRecord(record, 'create'),
    ))
    signals.forEach((record) => transaction.set(
      coreRepositories.signals.reference(record.id),
      encodeCoreRecord(record, 'create'),
    ))
    transaction.set(receiptRef, encodeCoreRecord(receipt, 'create'))

    return { applied: true }
  })
}

export const applyReviewResolution = async (
  write: ReviewResolutionWrite,
): Promise<{ applied: boolean }> => {
  const reviewItem = reviewItemSchema.parse(write.reviewItem)
  const capture = captureSchema.parse(write.capture)
  const receipt = automationReceiptSchema.parse(write.receipt)
  const action = write.action ? actionSchema.parse(write.action) : undefined
  const contextRecord = write.contextRecord ? contextRecordSchema.parse(write.contextRecord) : undefined
  const outputs = [action, contextRecord].filter((record): record is Action | ContextRecord => Boolean(record))

  assertSameOwner(capture.userId, [reviewItem, receipt, ...outputs])

  return runTransaction(db, async (transaction) => {
    const reviewRef = coreRepositories.reviewItems.reference(reviewItem.id)
    const captureRef = coreRepositories.captures.reference(capture.id)
    const receiptRef = coreRepositories.automationReceipts.reference(receipt.id)
    const [savedReview, savedCapture, savedReceipt] = await Promise.all([
      transaction.get(reviewRef),
      transaction.get(captureRef),
      transaction.get(receiptRef),
    ])

    if (!savedReview.exists() || !savedCapture.exists() || !savedReceipt.exists()) {
      throw new Error('Review resolution requires its saved review, capture, and receipt.')
    }

    const persistedReview = coreRepositories.reviewItems.parse(savedReview.data())
    const persistedCapture = coreRepositories.captures.parse(savedCapture.data())
    const persistedReceipt = coreRepositories.automationReceipts.parse(savedReceipt.data())

    if (persistedReview.status !== 'pending') {
      return { applied: false }
    }

    if (
      persistedReview.updatedAt !== write.expectedReviewUpdatedAt ||
      persistedCapture.updatedAt !== write.expectedCaptureUpdatedAt ||
      persistedReceipt.updatedAt !== write.expectedReceiptUpdatedAt
    ) {
      throw new Error('Review context changed before the resolution was applied.')
    }

    if (
      persistedReview.userId !== capture.userId ||
      persistedCapture.userId !== capture.userId ||
      persistedReceipt.userId !== capture.userId
    ) {
      throw new Error('Review resolution owner does not match its saved records.')
    }

    const savedContext = contextRecord && write.expectedContextUpdatedAt
      ? await transaction.get(coreRepositories.contextRecords.reference(contextRecord.id))
      : null
    if (write.expectedContextUpdatedAt) {
      if (!savedContext?.exists()) {
        throw new Error('Review resolution context was not found.')
      }
      const persistedContext = coreRepositories.contextRecords.parse(savedContext.data())
      if (persistedContext.userId !== capture.userId) {
        throw new Error('Review resolution context owner does not match the capture.')
      }
      if (persistedContext.updatedAt !== write.expectedContextUpdatedAt) {
        throw new Error('Review context changed before the resolution was applied.')
      }
    }

    if (action) {
      transaction.set(coreRepositories.actions.reference(action.id), encodeCoreRecord(action, 'create'))
    }
    if (contextRecord) {
      transaction.set(
        coreRepositories.contextRecords.reference(contextRecord.id),
        encodeCoreRecord(
          contextRecord,
          savedContext ? 'update' : 'create',
          savedContext?.data().createdAt,
        ),
      )
    }

    transaction.set(
      reviewRef,
      encodeCoreRecord(reviewItem, 'update', savedReview.data().createdAt),
    )
    transaction.set(
      captureRef,
      encodeCoreRecord(capture, 'update', savedCapture.data().createdAt),
    )
    transaction.set(
      receiptRef,
      encodeCoreRecord(receipt, 'update', savedReceipt.data().createdAt),
    )

    return { applied: true }
  })
}
